import React from 'react';
import { LayoutDashboard, CalendarDays, Clock, UserPlus, Users, TrendingUp, Settings as SettingsIcon, Bell, Package, FlaskConical, Wallet, Gift, Shield, Globe, Megaphone, ShieldCheck, Brain, Pill, HelpCircle, UserCog, BookOpen, ClipboardList, CheckSquare, UserCheck, FileEdit, Award, BookMarked, ScrollText, Network, UserSquare2, Database, ShieldAlert } from 'lucide-react';
import { theme, navItem } from '../theme';
import { useLang } from '../i18n';

/**
 * Collapsible sidebar.
 *
 * Props:
 *   activeTab        — current tab key
 *   setActiveTab     — setter
 *   resetViews       — clears in-flight transient state when switching tabs
 *   currentUser      — { fullName, role }
 *   handleLogout     — logout handler
 *   onSettingsOpen   — optional callback fired when Settings tab is clicked
 *   collapsed        — boolean
 *   setCollapsed     — setter (sidebar auto-collapses on mouse leave)
 */
// ⚠ LOCKED SIDEBAR — 11 items final. Do NOT add/remove without user approval.
// Sub-features must nest inside their parent hub (e.g. payslips inside Staff,
// purchase-orders inside Pharmacy, compliance reports inside Reports).
// See memory/feedback_sidebar_lock.md
export const NAV_ITEMS = [
  { key:'dashboard',      icon: LayoutDashboard, label:'Dashboard'  },
  { key:'calendar',       icon: CalendarDays,    label:'Calendar'   },
  { key:'list',           icon: Users,           label:'Patients'   },
  { key:'clinical-hub',   icon: Brain,           label:'Clinical'   },
  { key:'billing-hub',    icon: Wallet,          label:'Billing'    },
  { key:'pharmacy-hub',   icon: Pill,            label:'Pharmacy'   },
  { key:'staff-hub',      icon: UserCog,         label:'Staff'      },
  { key:'marketing-hub',  icon: Megaphone,       label:'Marketing'  },
  { key:'analytics',      icon: TrendingUp,      label:'Reports'    },
  { key:'compliance',     icon: ShieldCheck,     label:'Admin'      },
  { key:'settings',       icon: SettingsIcon,    label:'Settings'   },
];

const Sidebar = ({
  activeTab, setActiveTab, resetViews,
  currentUser, handleLogout, onSettingsOpen,
  collapsed, setCollapsed,
}) => {
  const { t } = useLang();
  return (
    <aside
      onMouseEnter={()=>setCollapsed(false)}
      onMouseLeave={()=>setCollapsed(true)}
      style={{
        width: collapsed ? '68px' : '232px',
        backgroundColor: theme.surface,
        borderRight: `1px solid ${theme.border}`,
        padding: '20px 12px',
        display:'flex', flexDirection:'column',
        flexShrink: 0,
        transition: 'width 0.2s ease',
        overflow: 'hidden',
      }}>
      {/* Logo */}
      <div style={{display:'flex', alignItems:'center', gap:'10px', padding:'4px 6px 22px', borderBottom:`1px solid ${theme.borderLight}`, marginBottom:'18px'}}>
        <div style={{
          width:'36px', height:'36px',
          background:`linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
          borderRadius:'10px',
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:'1.2rem', fontWeight:'700', color:'white',
          boxShadow:'0 2px 6px rgba(0,181,184,0.3)',
          flexShrink: 0,
        }}>S</div>
        {!collapsed && (
          <div style={{whiteSpace:'nowrap', overflow:'hidden'}}>
            <div style={{fontSize:'0.95rem', fontWeight:'700', color:theme.navy, lineHeight:1.2}}>Siddharth</div>
            <div style={{fontSize:'0.72rem', color:theme.textMuted, fontWeight:'500'}}>Dental Clinic</div>
          </div>
        )}
      </div>

      {!collapsed && (
        <div style={{fontSize:'0.68rem', color:theme.textSubtle, fontWeight:'700', textTransform:'uppercase', letterSpacing:'0.06em', padding:'0 12px 8px', whiteSpace:'nowrap'}}>
          Main Menu
        </div>
      )}

      <nav style={{ display:'flex', flexDirection:'column', gap:'2px', flex:1 }}>
        {NAV_ITEMS.map(({ key, icon: Icon, label: rawLabel }) => {
          const active = activeTab === key;
          const label = t(rawLabel);
          return (
            <div key={key}
              title={collapsed ? label : undefined}
              onClick={()=>{
                setActiveTab(key); resetViews();
                if (key === 'settings' && onSettingsOpen) onSettingsOpen();
              }}
              style={{
                ...navItem,
                padding: collapsed ? '11px 0' : '11px 14px',
                justifyContent: collapsed ? 'center' : 'flex-start',
                backgroundColor: active ? theme.primaryLight : 'transparent',
                color: active ? theme.primaryDark : theme.textMuted,
                fontWeight: active ? '600' : '500',
              }}
              onMouseEnter={e=>{ if(!active) e.currentTarget.style.backgroundColor='#f7f9fc'; }}
              onMouseLeave={e=>{ if(!active) e.currentTarget.style.backgroundColor='transparent'; }}>
              <Icon size={18} strokeWidth={active ? 2.4 : 2}/>
              {!collapsed && <span style={{whiteSpace:'nowrap'}}>{label}</span>}
              {!collapsed && active && <div style={{marginLeft:'auto', width:'4px', height:'18px', backgroundColor:theme.primary, borderRadius:'2px'}}/>}
            </div>
          );
        })}
      </nav>

      {/* User card */}
      <div style={{
        marginTop:'12px',
        padding: collapsed ? '8px' : '14px',
        backgroundColor: theme.bg,
        borderRadius: theme.radius,
        border: `1px solid ${theme.border}`,
      }}>
        <div style={{display:'flex', alignItems:'center', gap:'10px', marginBottom: collapsed ? 0 : '10px', justifyContent: collapsed ? 'center' : 'flex-start'}}>
          <div title={currentUser.fullName} style={{
            width:'36px', height:'36px',
            background: `linear-gradient(135deg, ${theme.purple} 0%, ${theme.pink} 100%)`,
            borderRadius:'50%',
            display:'flex', alignItems:'center', justifyContent:'center',
            color:'white', fontWeight:'700', fontSize:'0.85rem',
            flexShrink: 0,
          }}>
            {(currentUser.fullName||'U').charAt(0).toUpperCase()}
          </div>
          {!collapsed && (
            <div style={{flex:1, minWidth:0}}>
              <div style={{fontWeight:'600', color:theme.navy, fontSize:'0.82rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{currentUser.fullName}</div>
              <div style={{fontSize:'0.7rem', color:theme.textMuted}}>{({
                super_admin:   'Super Admin',
                owner:         'Owner',
                admin:         'Administrator',
                doctor:        'Doctor',
                receptionist:  'Receptionist',
                assistant:     'Assistant',
                staff:         'Staff',
              })[currentUser.role] || 'Staff'}</div>
            </div>
          )}
        </div>
        {!collapsed && currentUser?.tenantId && (
          <div
            onClick={() => { navigator.clipboard?.writeText(currentUser.tenantId); }}
            title="Click to copy tenant ID"
            style={{
              fontSize: '0.64rem',
              color: theme.textMuted,
              fontFamily: 'monospace',
              padding: '4px 6px',
              marginBottom: 6,
              background: theme.surface,
              border: `1px dashed ${theme.border}`,
              borderRadius: theme.radiusSm,
              cursor: 'pointer',
              textAlign: 'center',
              wordBreak: 'break-all',
              lineHeight: 1.3,
            }}>
            <div style={{ color: theme.primary, fontWeight: 600, letterSpacing: '0.4px' }}>TENANT ID</div>
            <div>{currentUser.tenantId}</div>
          </div>
        )}
        {!collapsed && (
          <button onClick={handleLogout}
            style={{
              width:'100%', padding:'7px',
              backgroundColor:'transparent',
              color:theme.danger,
              border:`1px solid ${theme.border}`,
              borderRadius: theme.radiusSm,
              cursor:'pointer',
              fontWeight:'600', fontSize:'0.78rem',
            }}>
            Logout
          </button>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
